import React from 'react';

import { themeStore } from '@neptune/shared/theme-domain';
import { DEFAULT_THEME, THEME_ALIASES, ThemeAlias } from '@neptune/shared/venus-domain';

export const useApplicationTheme = ({
  synchronizeTheme = false,
}: {
  synchronizeTheme?: boolean;
} = {}) => {
  const [actualTheme, setActualTheme] = React.useState(themeStore.getTheme);

  const rawChangeTheme = React.useCallback(() => {
    setActualTheme(themeStore.getTheme());
  }, []);

  const changeTheme = React.useCallback(
    (theme: string) => {
      themeStore.setTheme(theme as ThemeAlias);
      rawChangeTheme();
      window.dispatchEvent(new Event('storage'));
    },
    [rawChangeTheme],
  );

  const onSystemThemeChange = React.useCallback((event: MediaQueryListEvent) => {
    const userTheme = themeStore.getTheme();

    if (userTheme !== DEFAULT_THEME) {
      return;
    }

    const newTheme = event.matches ? THEME_ALIASES.DARK : THEME_ALIASES.LIGHT;
    setActualTheme(newTheme);
  }, []);

  React.useEffect(() => {
    if (!synchronizeTheme) {
      return;
    }

    rawChangeTheme();
    window.addEventListener('storage', rawChangeTheme);

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', onSystemThemeChange);

    return () => {
      window.removeEventListener('storage', rawChangeTheme);
      mediaQuery.removeEventListener('change', onSystemThemeChange);
    };
  }, [synchronizeTheme, rawChangeTheme, onSystemThemeChange]);

  return { theme: actualTheme, changeTheme };
};
